import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TableInstance } from 'react-table';

import { TableCellClickProvider } from '@work4all/components/lib/dataDisplay/basic-table/components/row-render/hooks/use-cell-click';

import { useParams, useUser } from '@work4all/data';

import { LookUp } from '@work4all/models/lib/Classes/LookUp.entity';
import { Project } from '@work4all/models/lib/Classes/Project.entity';
import { SortDirection } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import {
  OpenMaskOverride,
  useParameterizedOpenMask,
} from '../../../containers/mask-overlays/mask-overlay/hooks/use-open-mask';
import { useStringifyTableGroupFilter } from '../../../hooks';
import { settings, useSetting } from '../../../settings';
import { IEntityTable } from '../entity-table/EntityTable';
import { EntityTableV8 } from '../entity-table/EntityTableV8';
import { useEntityDataTableV8 } from '../entity-table/use-entity-data-table-v8';
import { useFileActions } from '../FileActions';
import { GridStateProvider } from '../grid/GridStateProvider';
import { ProjectPlanningDataProvider } from '../planning-columns/components/ProjectPlanningDataProvider';
import { useProjectTableAreas } from '../project-table/hooks/use-project-table-areas';
import { useProjectTableSchema } from '../project-table/hooks/use-project-table-schema';
import { ProjectPlannedTimesCell } from '../ProjectPlannedTimesCell';
import { ProjectPlanningCell } from '../ProjectPlanningCell';
import { ProjectTimeTrackingCell } from '../ProjectTimeCell';
import { useTableLayoutState } from '../table-layout';

import { ProjectControllingDataProvider } from './ProjectControllingDataProvider';

type IProjectTableCustomColumns = Record<string, never>;

const defaultSort = [
  { field: 'startDateOuter', direction: SortDirection.DESCENDING },
];

const secondarySort = [
  { field: 'number', direction: SortDirection.DESCENDING },
];

export const DEFAULT_COLS_IDS = [
  'name',
  'projectStatus.id',
  'projectPlannedTimes',
  'projectTimeTracking',
  'projectControllingPlanned',
  'projectControllingActual',
  'projectControllingComparison',
];

interface ProjectControllingTableProps extends IEntityTable {
  removedColIds?: string[];
  defaultColIds?: string[];
}

export const ProjectControllingTable = React.forwardRef<
  TableInstance,
  ProjectControllingTableProps
>(function ProjectTable(props, ref) {
  const { entityType } = useParams();

  const { set: setPlanned } = useSetting(
    settings.projectControllingcalPlannedColumns()
  );
  const { set: setActual } = useSetting(
    settings.projectControllingcalActualColumns()
  );

  useEffect(() => {
    switch (entityType) {
      case 'targetActualComparison': {
        setPlanned({
          dbinEUR: true,
          dbinPROCENT: true,
          externalcontribution: true,
          internalcontribution: true,
          sales: true,
          totalcosts: true,
        });
        setActual({
          costswithoutreceipt: true,
          dbinEUR: true,
          dbinPROCENT: true,
          externalcontribution: true,
          internalcontribution: true,
          sales: true,
          totalcosts: true,
        });
        break;
      }
      case 'contributionMargin': {
        setPlanned({
          dbinEUR: true,
          dbinPROCENT: true,
          externalcontribution: true,
          internalcontribution: true,
          sales: true,
          totalcosts: true,
        });
        setActual({
          costswithoutreceipt: true,
          dbinEUR: true,
          dbinPROCENT: true,
          externalcontribution: true,
          internalcontribution: true,
          sales: true,
          totalcosts: true,
        });
        break;
      }
    }
  }, []);

  return (
    <GridStateProvider>
      <ProjectControllingTableInner {...props} ref={ref} />
    </GridStateProvider>
  );
});

export const ProjectControllingTableInner = React.forwardRef<
  TableInstance,
  ProjectControllingTableProps
>(function ProjectTableInner(props) {
  const user = useUser();

  const [selectedProjectList, setSelectedProjectList] = useState<Project[]>([]);
  const [selectedUtilizationUserIds, setSelectedUtilizationUserIds] = useState(
    []
  );

  const [selectedRessourceClassCell, setSelectedRessourceClassCell] = useState<{
    project: Project;
    ressourceClass: LookUp;
  }>(null);

  const handleUtilizationClick = useCallback((userIds, project) => {
    setSelectedUtilizationUserIds(userIds);
    setSelectedProjectList([project]);
  }, []);

  const schema = useProjectTableSchema({
    projectPlanningProps: {
      selectedRessourceClassCell,
      onCellSelect: setSelectedRessourceClassCell,
      onUtilizationClick: handleUtilizationClick,
    },
    useProjectControllingColumns: true,
    removedColIds: props.removedColIds,
    defaultColIds: props.defaultColIds ?? DEFAULT_COLS_IDS,
  });

  const { entity: entityType } = schema;

  const layoutState = useTableLayoutState();
  const [layout] = layoutState;

  const forceRequestFields: Project = useMemo<Project>(() => {
    if (!user.isMaster) return props.forceRequestFields;
    return {
      ...props.forceRequestFields,
      projectAccessGroups: [
        {
          id: null,
        },
      ],
      childProjects: [
        {
          id: null,
        },
      ],
    } as Project;
  }, [props.forceRequestFields, user.isMaster]);

  const dataTable = useEntityDataTableV8<Project, IProjectTableCustomColumns>({
    schema,
    defaultSort,
    secondarySort,
    forceRequestFields,
    rowModifiers: (value) => {
      return {
        isFaded: value.projectStatus?.closedStatus === -1,
      };
    },
    cells: {
      ProjectPlanning: ProjectPlanningCell,
      ProjectTimeTracking: ProjectTimeTrackingCell,
      ProjectPlannedTimes: ProjectPlannedTimesCell,
    },
    onRowsSelected: (rows) => {
      /**Only update if selection has changed to prevent loop */
      const newProjects = rows.map((r) => r.original as Project);
      if (JSON.stringify(newProjects) !== JSON.stringify(selectedProjectList)) {
        setDate(
          new Date(
            newProjects?.[0]?.startDateInner ||
              newProjects?.[0]?.startDateOuter ||
              new Date()
          )
        );
        setSelectedProjectList(newProjects);
      }
    },
  });

  const { fileActionDefinitions, openInNewTab } = useFileActions({
    entity: entityType,
    table: dataTable.table,
  });

  const actions = useMemo(() => {
    return {
      openInNewTab: openInNewTab,
      custom: {
        left: [...fileActionDefinitions],
      },
      createWidget: false,
    };
  }, [fileActionDefinitions, openInNewTab]);

  const [date, setDate] = useState<Date>(new Date());

  const areas = useProjectTableAreas({
    date,
    selectedProjectList,
    selectedRessourceClassCell,
    selectedUtilizationUserIds,
    table: dataTable.table,
    showBottomArea: false,
  });

  const presetFields = useStringifyTableGroupFilter<Project>({
    groupKey: 'group',
    groupIdKey: 'groupId',
    table: dataTable.table,
  });

  const onOpenMask = useParameterizedOpenMask({
    entityType: Entities.project,
    params: { create: { presetFields } },
  });

  return (
    <ProjectControllingDataProvider projectList={dataTable.data}>
      <ProjectPlanningDataProvider projectList={dataTable.data}>
        <TableCellClickProvider onDateCellClick={setDate}>
          <OpenMaskOverride value={{ onOpenMask }}>
            <EntityTableV8
              layout={layout}
              areas={areas}
              actions={actions}
              {...dataTable}
              {...props}
            />
          </OpenMaskOverride>
        </TableCellClickProvider>
      </ProjectPlanningDataProvider>
    </ProjectControllingDataProvider>
  );
});
